import { isV2Path } from "../../universal/v2Routes";
export const CANONICAL_BASE_URL = "https://www.ouro.is";

export const OG_IMAGE_URL =
  "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/ouro-social-card.png";

export const ANTI_CHEAT_PAGE_TITLE = "Ouro Anti-Cheat";
export const ANTI_CHEAT_PAGE_DESCRIPTION =
  "Learn how Ouro's server-side anti-cheat collects tick-by-tick gameplay data throughout live matches and flags suspicious behavior patterns.";

export const DISCORD_INVITE_URL = "https://discord.gg/eXduKXZ3";
export const X_PROFILE_URL = "https://x.com/OuroCSMM";
export const SUPPORT_EMAIL = "support@ouro.is";
export const PRIVACY_EMAIL = "support@ouro.is";

export const PUBLIC_NAV_LINKS = [
  { label: "How it works", to: "/how-it-works" },
  { label: "Match Night", to: "/match-night" },
  { label: "Pricing", to: "/pricing" },
  { label: "FAQ", to: "/faq" },
];

export const SHARED_LEGAL_LINKS = [
  { label: "About", to: "/about-us" },
  { label: "Terms", to: "/terms-of-use" },
  { label: "Privacy", to: "/privacy" },
  { label: "GDPR", to: "/data-protection-policy-and-gdpr" },
];

export const PUBLIC_FOOTER_LEGAL_LINKS = SHARED_LEGAL_LINKS.filter(
  (link) => link.to !== "/about-us"
);

export const PUBLIC_FOOTER_SECTIONS = [
  {
    title: "Product",
    links: [
      { label: "How it works", to: "/how-it-works" },
      { label: "Match Night", to: "/match-night" },
      { label: "Reputation", to: "/reputation" },
      { label: "Anti-Cheat", to: "/anti-cheat" },
      { label: "Trust & Safety", to: "/trust-and-safety" },
      { label: "Pricing", to: "/pricing" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Ouro", to: "/about-us" },
      { label: "Contact", to: "/contact" },
      { label: "FAQ", to: "/faq" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Submit a Ticket", to: "/support" },
      { label: "Privacy Requests", href: `mailto:${PRIVACY_EMAIL}` },
      { label: "Discord", href: DISCORD_INVITE_URL },
    ],
  },
  {
    title: "Legal",
    links: PUBLIC_FOOTER_LEGAL_LINKS,
  },
];

export const PUBLIC_MARKETING_PATHS = new Set([
  "/",
  "/about-us",
  "/anti-cheat",
  "/auth/playsafe/status",
  "/auth/playsafe/connect-steam",
  "/auth/steam/status",
  "/contact",
  "/faq",
  "/how-it-works",
  "/match-night",
  "/pricing",
  "/privacy",
  "/reputation",
  "/terms-of-use",
  "/support",
  "/trust-and-safety",
  "/data-protection-policy-and-gdpr",
]);

const APP_SHELL_PATH_PREFIXES = [
  "/account",
  "/leaderboard",
  "/lobby",
  "/match",
  "/matches",
  "/membership/plans",
  "/players",
  "/profile",
  "/reports",
  "/settings",
  "/tactical-profile",
];

export const PUBLIC_PAGE_META = {
  "/": {
    title: "Ouro Matchmaking for CS2",
    description:
      "Join the founding CS2 players building verified, reputation-backed matchmaking with coordinated regional queue windows.",
  },
  "/about-us": {
    title: "About Ouro",
    description:
      "Learn why Ouro exists and why trust, accountability, and match quality sit at the center of the platform.",
  },
  "/contact": {
    title: "Contact Ouro",
    description:
      "Get in touch for support, privacy requests, partnerships, or product feedback about Ouro Matchmaking.",
  },
  "/faq": {
    title: "Ouro FAQ",
    description:
      "Answers to common questions about verification, reputation, memberships, and how Ouro matchmaking works.",
  },
  "/how-it-works": {
    title: "How Ouro Works",
    description:
      "See how players join Ouro, get verified, coordinate regional queue windows, and build reputation over time.",
  },
  "/pricing": {
    title: "Ouro Pricing",
    description:
      "Compare Free, Premium, and Pro memberships and see which matchmaking controls unlock at each level.",
  },
  "/privacy": {
    title: "Privacy Policy",
    description:
      "Read Ouro's privacy policy and how the platform handles personal data, verification, and account information.",
  },
  "/reputation": {
    title: "Ouro Reputation System",
    description:
      "Understand the reputation system that rewards reliable players and adds accountability to every match.",
  },
  "/terms-of-use": {
    title: "Terms of Use",
    description:
      "Review the terms that govern access to Ouro Matchmaking and use of the platform.",
  },
  "/anti-cheat": {
    title: ANTI_CHEAT_PAGE_TITLE,
    description: ANTI_CHEAT_PAGE_DESCRIPTION,
  },
  "/auth/playsafe/status": {
    title: "PlaySafe ID Status",
    description:
      "Review why PlaySafe ID sign-in could not grant access to Ouro matchmaking.",
  },
  "/auth/playsafe/connect-steam": {
    title: "Connect Your Steam Account",
    description:
      "Link your Steam account to confirm your identity and unlock Ouro matchmaking.",
  },
  "/auth/steam/status": {
    title: "Steam Sign-in Status",
    description:
      "Review why Steam sign-in could not create an Ouro account, or continue after a signup warning.",
  },
  "/match-night": {
    title: "Ouro Match Night",
    description:
      "Reserve your spot in an upcoming Ouro match night with hand-vetted players and balanced matches.",
  },
  "/trust-and-safety": {
    title: "Trust & Safety",
    description:
      "How Ouro approaches verification, moderation, reporting, and fair-play enforcement for better matches.",
  },
  "/data-protection-policy-and-gdpr": {
    title: "GDPR and Data Protection",
    description:
      "Read Ouro's GDPR and data protection policy, including your rights and how to contact the team.",
  },
  "/support": {
    title: "Ouro Support",
    description:
      "Submit a support ticket for account, billing, matchmaking, or technical issues with Ouro Matchmaking.",
  },
};

export const FAQ_ITEMS = [
  {
    question: "Is Ouro fully live yet?",
    answer:
      "Ouro is live, but very early. The platform is currently focused on building the founding player base region by region so queues can open with enough density to feel good instead of random or empty.",
  },
  {
    question: "When can I queue?",
    answer:
      "While the player base grows, queues are coordinated in regional evening windows, currently around 6-10pm. As a region gets healthier density, those windows can expand toward more regular matchmaking.",
  },
  {
    question: "What if my region does not have enough players yet?",
    answer:
      "Join anyway if you believe in the platform. Early signups help show where demand is forming, and opening new game server locations is straightforward when a region proves player demand first.",
  },
  {
    question: "Is Steam sign-in safe?",
    answer:
      "Yes. Ouro uses official Steam OpenID for the main signup path. It is read-only and never asks for inventory, trade, market, API key, or Steam Guard access. Google is available only as a fallback if Steam gives you trouble.",
  },
  {
    question: "Is ID verification required?",
    answer:
      "No. A Steam account is all you need to join. Every account still goes through human review, and an optional ID check through didit.me adds to your reputation if you choose it.",
  },
  {
    question: "Is there an option for women who want to play with other women?",
    answer:
      "Yes. Women on Ouro can opt in to a soft preference that nudges the matchmaker toward putting them on a team with more women whenever it can do so without breaking team balance or slowing the queue. It is opt-in, women only, and never used as a hard filter — so it does not change the experience for men or push anyone out of a match. It is a small quality-of-life setting for a group that is too often overlooked in this game, designed so women's preferences get treated with the same respect as everyone else's map and server choices.",
  },
  {
    question: "What is the Impact Rating?",
    answer:
      "The Impact Rating is a single number that summarises how much you actually contributed in a match — not just kills. It looks at things like opening duels, trades, utility usage, and playing for the round, and combines them into a score centred around 1.0 (higher when you have a big game). You will see it on match pages, lobby cards, your profile, and your match history, with a short breakdown if you want to see what drove the number.",
  },
  {
    question: "Is reputation just another hidden score?",
    answer:
      "No. Reputation is meant to be understandable. It reflects behavior and reliability across matches so good players have a clearer signal of who they are queueing with.",
  },
  {
    question: "Can I use Ouro without paying?",
    answer:
      "Yes. Free access lets you join the founding community and help seed your region. Premium and Pro are for players who later want deeper control over matchmaking settings and queue behavior.",
  },
  {
    question: "When do memberships matter?",
    answer:
      "Memberships matter when you want more control. They unlock deeper map and server filtering, queue advantages, and higher-end account tools for committed players.",
  },
  {
    question: "Does Ouro use FaceIT data?",
    answer:
      "Yes. Ouro can import FaceIT information during verification so your account starts from a more relevant competitive baseline instead of a blank slate.",
  },
  {
    question: "How does Ouro handle cheaters?",
    answer:
      "Ouro runs a server-side anti-cheat that collects tick-by-tick gameplay data during live matches and flags suspicious behavior patterns like prefire angles, wallhit timing, and reaction speeds that look too strong to be human. Combined with account verification and reputation tracking, the system makes bans meaningful and hard to evade.",
  },
];

export function isMarketingRoute(pathname = "") {
  if (isV2Path(pathname)) {
    return false;
  }

  if (PUBLIC_MARKETING_PATHS.has(pathname)) {
    return true;
  }

  return !APP_SHELL_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function getPublicPageMeta(pathname = "") {
  return (
    PUBLIC_PAGE_META[pathname] || {
      title: "Ouro Matchmaking",
      description:
        "Ouro is a trust-focused Counter-Strike matchmaking platform built around verified players and better match quality.",
    }
  );
}
